import type { Corpus, Section } from '../models/Corpus';
import { LocalCorpus } from './LocalCorpus';
import { PublicDomainCorpus } from './PublicDomainCorpus';
import { loadBibleData, getTableOfContents, getChapter, searchBible, getBookIdFromSlug } from '../offline/douay/douaySource';

// 1. Douay Corpus Wrapper
class DouayCorpus implements Corpus {
    id = 'douay_rheims';
    title = 'Douay-Rheims Bible';
    author = 'God (et al)';
    language = 'en';
    source = { type: 'internal' as const };

    async load() { return await loadBibleData(); }

    getSections(): Section[] {
        return getTableOfContents().map((s: any) => ({ ...s, title: s.name }));
    }

    getChapter(secId: string, ch: number) {
        return getChapter(secId, ch);
    }

    getUnitText(secId: string, ch: number) {
        const c = getChapter(secId, ch);
        if (!c) return null;
        return Object.entries(c).map(([v, t]) => `${v} ${t}`).join("\n");
    }

    async search(q: string) { return searchBible(q); }

    resolveSectionId(slug: string) { return getBookIdFromSlug(slug); }
}

// 2. Placeholder Buddhist Corpus
class PlaceholderCorpus implements Corpus {
    id = 'buddhist-teachings';
    title = 'Buddhist Teachings';
    author = 'Various';
    language = 'en';
    source = { type: 'internal' as const }; // Placeholder

    async load() { return true; }
    getSections() { return []; }
    getChapter() { return null; }
    getUnitText() { return null; }
    async search() { return []; }
    resolveSectionId() { return undefined; }
}

// 3. Registry
class CorpusRegistryService {
    private corpora: Map<string, Corpus> = new Map();
    private activeId: string = 'public_domain_gutendex';

    constructor() {
        // Register built-ins
        this.register(new DouayCorpus());
        this.register(new PlaceholderCorpus());
        this.register(new PublicDomainCorpus());

        // Load persisted selection
        const saved = localStorage.getItem('active_corpus_id');
        if (saved && this.corpora.has(saved)) {
            this.activeId = saved;
        }
    }

    register(c: Corpus) {
        this.corpora.set(c.id, c);
    }

    async init() {
        // Load Siddhartha Manifest
        try {
            const res = await fetch('/corpora/siddhartha/manifest.json');
            if (res.ok) {
                const manifest = await res.json();
                // Override source path if needed to ensure we use HTML
                if (manifest.source && manifest.source.path) {
                    // Fix path if relative or ensure it's correct
                    // Manifesto says "path": "/corpora/siddhartha/siddhartha.epub" on disk
                    // User says: use HTML.
                    // We will trust the user requirement to override or expect HTML
                    const c = new LocalCorpus({
                        id: manifest.id,
                        title: manifest.title,
                        author: manifest.author,
                        language: manifest.language,
                        source: {
                            type: 'html',
                            path: '/corpora/siddhartha/siddhartha.html' // Force HTML per user spec
                        }
                    });
                    this.register(c);
                }
            } else {
                console.warn("Siddhartha manifest not found, skipping.");
            }
        } catch (e) {
            console.error("Failed to load Siddhartha manifest", e);
        }

        // Ensure active ID is valid after registration
        const saved = localStorage.getItem('active_corpus_id');
        if (saved && this.corpora.has(saved)) {
            this.activeId = saved;
        }
    }

    getAll(): Corpus[] {
        return Array.from(this.corpora.values());
    }

    get(id: string): Corpus | undefined {
        return this.corpora.get(id);
    }

    getActive(): Corpus {
        return this.corpora.get(this.activeId) || this.corpora.get('public_domain_gutendex')!;
    }

    setActive(id: string) {
        if (this.corpora.has(id)) {
            this.activeId = id;
            localStorage.setItem('active_corpus_id', id);
            return true;
        }
        return false;
    }
}

export const CorpusService = new CorpusRegistryService();
